import { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Gavel, Paperclip, ImageSquare, CheckCircle, X, Warning, Hourglass } from '@phosphor-icons/react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const STATUS_STYLE = {
  open: 'bg-yellow-100 text-yellow-700',
  under_review: 'bg-blue-100 text-blue-700',
  awaiting_evidence: 'bg-amber-100 text-amber-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-600',
};

const DisputeDetail = ({ adminContext = false }) => {
  const { dispute_id } = useParams();
  const navigate = useNavigate();
  const [dispute, setDispute] = useState(null);
  const [me, setMe] = useState(null);
  const [note, setNote] = useState('');
  const [evidenceNote, setEvidenceNote] = useState('');
  const [decision, setDecision] = useState('release_to_traveler');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const fileInputRef = useRef(null);

  const load = () => {
    fetch(`${API}/disputes/${dispute_id}`, { credentials: 'include' })
      .then((r) => r.json()).then(setDispute);
  };

  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: 'include' }).then((r) => r.json()).then(setMe);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispute_id]);

  if (!dispute) return <div className="min-h-screen flex items-center justify-center text-gray-400">Loading…</div>;

  const closed = ['resolved', 'closed'].includes(dispute.status);
  const isParty = me && [dispute.sender_id, dispute.traveler_id].includes(me.user_id);

  const respond = async () => {
    if (!note.trim()) return;
    const res = await fetch(`${API}/disputes/${dispute_id}/respond`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ note }),
    });
    if (!res.ok) { toast.error('Failed to post message'); return; }
    setNote(''); load();
  };

  const uploadEvidence = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('note', evidenceNote);
    const res = await fetch(`${API}/disputes/${dispute_id}/evidence`, {
      method: 'POST', credentials: 'include', body: fd,
    });
    if (!res.ok) {
      const d = await res.json();
      toast.error(typeof d.detail === 'string' ? d.detail : 'Upload failed'); return;
    }
    toast.success('Evidence uploaded');
    setEvidenceNote('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    load();
  };

  const resolve = async () => {
    const body = { decision, resolution_notes: resolutionNotes };
    if (decision === 'partial_refund') body.refund_amount = parseFloat(refundAmount) || 0;
    const res = await fetch(`${API}/admin/disputes/${dispute_id}/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!res.ok) { const d = await res.json(); toast.error(typeof d.detail === 'string' ? d.detail : 'Resolve failed'); return; }
    toast.success('Resolution recorded');
    load();
  };

  const closeDispute = async () => {
    if (!window.confirm('Close this dispute permanently?')) return;
    await fetch(`${API}/admin/disputes/${dispute_id}/close`, { method: 'POST', credentials: 'include' });
    toast.success('Dispute closed');
    load();
  };

  const fullUrl = (u) => (u?.startsWith('http') ? u : `${API.replace(/\/api$/, '')}${u}`);

  return (
    <div className={adminContext ? '' : 'min-h-screen bg-[#F9FAFB] p-6 md:p-10'}>
      <Link to={adminContext ? '/admin/disputes' : '/disputes'} className="text-sm text-[#6D28D9] hover:underline mb-4 inline-block" data-testid="back-link">← Back</Link>

      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Gavel size={22} weight="duotone" className="text-[#6D28D9]" />
              <h1 className="text-2xl font-extrabold text-gray-900" data-testid="dispute-title">{dispute.reason}</h1>
            </div>
            <p className="text-xs text-gray-500 font-mono">Dispute {dispute.dispute_id} · Booking {dispute.booking_id}</p>
          </div>
          <span className={`text-[11px] font-bold uppercase px-3 py-1 rounded-full ${STATUS_STYLE[dispute.status]}`} data-testid="dispute-status">
            {dispute.status.replace(/_/g, ' ')}
          </span>
        </div>
        {dispute.description && <p className="text-sm text-gray-700 mt-3 leading-relaxed" data-testid="dispute-description">{dispute.description}</p>}
        {dispute.admin_decision && (
          <div className="mt-4 p-3 rounded-lg bg-purple-50 border border-purple-200" data-testid="admin-decision">
            <p className="text-xs font-bold uppercase text-[#6D28D9] tracking-wider mb-1">Resolution</p>
            <p className="text-sm font-semibold">{dispute.admin_decision.replace(/_/g, ' ')}</p>
            <p className="text-xs text-gray-700 mt-1">{dispute.resolution_notes}</p>
            {dispute.refund_amount > 0 && <p className="text-xs text-gray-700 mt-1">Refund: ${(dispute.refund_amount || 0).toFixed(2)} · Payout: ${(dispute.payout_amount || 0).toFixed(2)}</p>}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Timeline */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-bold mb-4">Discussion & Evidence</h2>
          <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2" data-testid="dispute-timeline">
            {(dispute.messages || []).length === 0 && (dispute.evidence || []).length === 0 && (
              <p className="text-sm text-gray-400 text-center py-8">No messages or evidence yet.</p>
            )}
            {(dispute.messages || []).map((m) => (
              <div key={m.message_id} className={`p-3 rounded-lg ${m.is_admin ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-xs font-bold text-gray-900">{m.user_name || 'User'}</p>
                  {m.is_admin && <span className="text-[10px] font-bold uppercase bg-[#6D28D9] text-white px-1.5 py-0.5 rounded">Admin</span>}
                  <span className="text-[10px] text-gray-400 ml-auto">{new Date(m.timestamp).toLocaleString()}</span>
                </div>
                <p className="text-sm text-gray-700">{m.note}</p>
              </div>
            ))}
            {(dispute.evidence || []).map((e) => (
              <div key={e.evidence_id} className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <ImageSquare size={14} weight="bold" className="text-blue-600" />
                  <p className="text-xs font-bold text-gray-900">{e.user_name || 'User'} uploaded evidence</p>
                  <span className="text-[10px] text-gray-400 ml-auto">{new Date(e.uploaded_at).toLocaleString()}</span>
                </div>
                <img src={fullUrl(e.url)} alt="evidence" className="rounded max-h-48 mb-2" />
                {e.note && <p className="text-xs text-gray-700">{e.note}</p>}
              </div>
            ))}
          </div>

          {/* Compose */}
          {!closed && isParty && (
            <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
              <Textarea placeholder="Add a message…" rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="dispute-message-input" />
              <div className="flex gap-2">
                <Button onClick={respond} className="dropio-gradient-cta text-white" data-testid="post-message">Post message</Button>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
                       onChange={(e) => e.target.files?.[0] && uploadEvidence(e.target.files[0])} />
                <Input placeholder="Evidence caption" value={evidenceNote} onChange={(e) => setEvidenceNote(e.target.value)} className="flex-1" data-testid="evidence-caption" />
                <Button onClick={() => fileInputRef.current?.click()} className="bg-white text-gray-900 border border-gray-200" data-testid="upload-evidence">
                  <Paperclip size={16} weight="bold" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Admin actions */}
        {adminContext && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="admin-actions">
            <h2 className="font-bold mb-4">Admin Decision</h2>
            {!closed ? (
              <div className="space-y-3">
                <div>
                  <Label>Decision</Label>
                  <select className="mt-1 w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm" value={decision} onChange={(e) => setDecision(e.target.value)} data-testid="decision-select">
                    <option value="release_to_traveler">Release funds to traveler</option>
                    <option value="refund_sender">Full refund to sender</option>
                    <option value="partial_refund">Partial refund</option>
                    <option value="escalate">Escalate (keep open)</option>
                  </select>
                </div>
                {decision === 'partial_refund' && (
                  <div>
                    <Label>Refund Amount (USD)</Label>
                    <Input type="number" step="0.01" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} data-testid="refund-amount" />
                  </div>
                )}
                <div>
                  <Label>Resolution Notes</Label>
                  <Textarea rows={3} value={resolutionNotes} onChange={(e) => setResolutionNotes(e.target.value)} data-testid="resolution-notes" />
                </div>
                <Button onClick={resolve} className="w-full dropio-gradient-cta text-white" data-testid="apply-decision">
                  Apply Decision
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-gray-600">Dispute is {dispute.status.replace(/_/g, ' ')}.</p>
                {dispute.status !== 'closed' && (
                  <Button onClick={closeDispute} className="w-full bg-gray-100 text-gray-900 hover:bg-gray-200" data-testid="close-dispute">
                    Close Permanently
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DisputeDetail;
